import { createColumnHelper, TableMeta } from '@tanstack/react-table';
import { History, Search } from 'lucide-react';
import { Dispatch, SetStateAction } from 'react';

import { isoDateFromTimestamp } from '@/portainer/filters/filters';

import { ExpandableDatatable } from '@@/datatables/ExpandableDatatable';
import { Button } from '@@/buttons';
import { JsonTree } from '@@/JsonTree';
import { mergeOptions } from '@@/datatables/extend-options/mergeOptions';
import { withColumnFilters } from '@@/datatables/extend-options/withColumnFilters';
import { multiple } from '@@/datatables/filter-types';
import { withMeta } from '@@/datatables/extend-options/withMeta';
import { filterHOC } from '@@/datatables/Filter';

import { getSortType } from './useActivityLogs';
import { ActivityLog, FilterState } from './types';
import { useTableFilterState } from './useTableFilterState';

const columnHelper = createColumnHelper<ActivityLog>();

const columns = [
  columnHelper.accessor('timestamp', {
    id: 'timestamp',
    header: 'Time',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? isoDateFromTimestamp(value) : '';
    },
  }),
  columnHelper.accessor('username', {
    id: 'username',
    header: 'User',
    enableColumnFilter: true,
    filterFn: multiple,
    meta: {
      filter: filterHOC<ActivityLog>('Filter by username', (_, id, tableMeta) =>
        getFilterOptions(id, tableMeta)
      ),
    },
  }),
  columnHelper.accessor('context', {
    id: 'context',
    header: 'Environment',
    enableColumnFilter: true,
    filterFn: multiple,
    meta: {
      filter: filterHOC<ActivityLog>(
        'Filter by environment',
        (_, id, tableMeta) => getFilterOptions(id, tableMeta)
      ),
    },
  }),
  columnHelper.accessor('action', {
    id: 'action',
    header: 'Action',
  }),
  columnHelper.accessor('payload', {
    id: 'payload',
    header: 'Payload',
    enableSorting: false,
    cell: ({ row, getValue }) =>
      getValue() ? (
        <Button
          color="link"
          onClick={() => row.toggleExpanded()}
          icon={Search}
          data-cy={`activity-logs-inspect_${row.index}`}
        >
          inspect
        </Button>
      ) : null,
  }),
];

export function ActivityLogsTable({
  dataset,
  currentPage,
  keyword,
  limit,
  onChangeKeyword,
  onChangeLimit,
  onChangePage,
  onChangeSort,
  sort,
  totalItems,
  filterState,
  onChangeFilterState,
  filterOptions,
}: {
  keyword: string;
  onChangeKeyword(keyword: string): void;
  sort: { id: string; desc: boolean } | undefined;
  onChangeSort(sort: { id: string; desc: boolean } | undefined): void;
  limit: number;
  onChangeLimit(limit: number): void;
  currentPage: number;
  onChangePage(page: number): void;
  totalItems: number;
  dataset?: Array<ActivityLog>;
  filterState: FilterState;
  onChangeFilterState: Dispatch<SetStateAction<FilterState>>;
  filterOptions?: {
    username: Array<string>;
    context: Array<string>;
  };
}) {
  const { tableFilterState, handleChangeFilters } = useTableFilterState(
    filterState,
    onChangeFilterState
  );

  return (
    <ExpandableDatatable<ActivityLog>
      title="Activity logs"
      titleIcon={History}
      columns={columns}
      dataset={dataset || []}
      isLoading={!dataset}
      settingsManager={{
        pageSize: limit,
        search: keyword,
        setPageSize: onChangeLimit,
        setSearch: onChangeKeyword,
        setSortBy: (id, desc) =>
          onChangeSort({ id: getSortType(id) || 'Timestamp', desc }),
        sortBy: sort
          ? {
              id: sort.id,
              desc: sort.desc,
            }
          : undefined,
      }}
      page={currentPage}
      onPageChange={onChangePage}
      isServerSidePagination
      totalCount={totalItems}
      disableSelect
      renderSubRow={(row) => <SubRow item={row.original} />}
      data-cy="activity-logs-datatable"
      extendTableOptions={mergeOptions(
        withColumnFilters(tableFilterState, handleChangeFilters),
        withMeta({
          filterOptions,
        })
      )}
    />
  );
}

function SubRow({ item }: { item: ActivityLog }) {
  return (
    <tr>
      <td colSpan={Number.MAX_SAFE_INTEGER}>
        <JsonTree data={item.payload} />
      </td>
    </tr>
  );
}

function getFilterOptions(id: string, tableMeta: TableMeta<ActivityLog>) {
  // Validate filterOptions existence and type
  if (
    !tableMeta ||
    !('filterOptions' in tableMeta) ||
    !tableMeta.filterOptions ||
    typeof tableMeta.filterOptions !== 'object' ||
    !(id in tableMeta.filterOptions)
  ) {
    return [];
  }
  return (
    tableMeta.filterOptions[id as keyof typeof tableMeta.filterOptions] || []
  );
}
